import { DotsThree, PencilSimple, Trash } from '@phosphor-icons/react';
import type { CommentDto } from '@reelplicate/api-client/model';
import {
  Avatar,
  ConfirmDialog,
  IconButton,
  Inline,
  Link,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Stack,
} from '@reelplicate/ui';
import { Fragment, useState } from 'react';

import { relativeTime } from '@/features/notifications/to-entry';

import { MentionComposer } from './mention-composer';
import { parseBody } from './mention-format';

/** A body with its @mentions drawn as bold brand-coloured names. */
export function CommentBody({ body }: { body: string }) {
  return (
    <p className="text-body-sm text-text-primary break-words whitespace-pre-wrap">
      {parseBody(body).map((part, index) =>
        'mention' in part ? (
          <span key={index} className="text-text-brand font-semibold">
            @{part.mention.name}
          </span>
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </p>
  );
}

/**
 * One comment (Figma "Comments / Comment" and "Deleted comment"): avatar,
 * name, time, "· Edited", the body, Reply on top-level comments, and ⋯ Edit /
 * Delete when it's yours (owners can delete anyone's).
 */
export function CommentItem({
  comment,
  workspaceId,
  isReply,
  onReply,
  onEdit,
  onDelete,
}: {
  comment: CommentDto;
  workspaceId: string;
  isReply?: boolean;
  onReply?: () => void;
  onEdit: (body: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (comment.deleted || comment.body === null) {
    return (
      <Inline gap={3} alignY="center">
        <span
          aria-hidden
          className="bg-bg-subtle text-icon-tertiary flex size-8 shrink-0 items-center justify-center rounded-full"
        >
          <Trash className="size-4" />
        </span>
        <p className="text-body-sm text-text-tertiary">This comment was deleted.</p>
      </Inline>
    );
  }

  const name = comment.author.name ?? 'Someone';
  return (
    <article aria-label={`Comment from ${name}`}>
      <Inline gap={3} alignY="top">
        <Avatar size={32} name={name} src={comment.author.imageUrl ?? undefined} alt="" />
        <Stack gap={1} className="min-w-0 flex-1">
          <Inline gap={2} alignY="center">
            <span className="text-body-sm-strong text-text-primary">{name}</span>
            <time
              dateTime={comment.createdAt}
              className="text-caption text-text-tertiary"
            >
              {relativeTime(new Date(comment.createdAt), new Date())}
            </time>
            {comment.editedAt && (
              <span className="text-caption text-text-tertiary">· Edited</span>
            )}
          </Inline>
          {editing ? (
            <MentionComposer
              workspaceId={workspaceId}
              initialBody={comment.body}
              submitLabel="Save"
              autoFocus
              onCancel={() => setEditing(false)}
              onSubmit={async (body) => {
                await onEdit(body);
                setEditing(false);
              }}
            />
          ) : (
            <CommentBody body={comment.body} />
          )}
          {!isReply && !editing && onReply && (
            <Inline>
              <Link asChild size="sm" tone="neutral">
                <button type="button" onClick={onReply}>
                  Reply
                </button>
              </Link>
            </Inline>
          )}
        </Stack>
        {(comment.canEdit || comment.canDelete) && !editing && (
          <Menu>
            <MenuTrigger asChild>
              <IconButton
                icon={DotsThree}
                size="sm"
                variant="ghost"
                aria-label={`More for ${name}’s comment`}
              />
            </MenuTrigger>
            <MenuContent align="end">
              {comment.canEdit && (
                <MenuItem leadingIcon={PencilSimple} onSelect={() => setEditing(true)}>
                  Edit
                </MenuItem>
              )}
              {comment.canDelete && (
                <MenuItem
                  type="danger"
                  leadingIcon={Trash}
                  onSelect={() => setConfirming(true)}
                >
                  Delete
                </MenuItem>
              )}
            </MenuContent>
          </Menu>
        )}
      </Inline>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        tone="danger"
        title="Delete this comment?"
        description="Replies stay, under a “This comment was deleted” note."
        confirmLabel="Delete comment"
        onConfirm={() => void onDelete()}
      />
    </article>
  );
}
